import { Box, Link } from '@mui/material';
import React from 'react';
import type { RenderElementProps, RenderLeafProps } from 'slate-react';
import styled from 'styled-components';

// Styled elements
const BlockQuote = styled.blockquote`
  margin: 8px 0;
  padding: 12px 16px;
  border-left: 4px solid #3b6fd9;
  background: rgba(59, 111, 217, 0.04);
  border-radius: 0 8px 8px 0;
  color: #4b5563;
  font-style: italic;
`;

const CodeBlock = styled.pre`
  margin: 8px 0;
  padding: 16px;
  background: #1e1e2e;
  border-radius: 8px;
  font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  font-size: 0.875rem;
  color: #cdd6f4;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const BulletedList = styled.ul`
  margin: 8px 0;
  padding-left: 24px;
  list-style-type: disc;

  li {
    margin: 4px 0;
  }
`;

const NumberedList = styled.ol`
  margin: 8px 0;
  padding-left: 24px;
  list-style-type: decimal;

  li {
    margin: 4px 0;
  }
`;

const ListItem = styled.li`
  line-height: 1.6;
`;

const HeadingOne = styled.h1`
  margin: 16px 0 8px;
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a1a2e;
  line-height: 1.3;
`;

const HeadingTwo = styled.h2`
  margin: 12px 0 6px;
  font-size: 1.375rem;
  font-weight: 600;
  color: #1a1a2e;
  line-height: 1.4;
`;

const Paragraph = styled.p`
  margin: 4px 0;
  line-height: 1.7;
`;

const InlineCode = styled.code`
  padding: 2px 6px;
  background: rgba(59, 111, 217, 0.08);
  border-radius: 4px;
  font-family: 'Fira Code', 'Monaco', 'Consolas', monospace;
  font-size: 0.875em;
  color: #3b6fd9;
`;

const StyledLink = styled(Link)`
  color: #3b6fd9;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #2952a3;
  }
`;

// Element renderer
export const Element: React.FC<RenderElementProps> = ({ attributes, children, element }) => {
  switch (element.type) {
    case 'block-quote':
      return <BlockQuote {...attributes}>{children}</BlockQuote>;
    case 'code-block':
      return <CodeBlock {...attributes}>{children}</CodeBlock>;
    case 'bulleted-list':
      return <BulletedList {...attributes}>{children}</BulletedList>;
    case 'numbered-list':
      return <NumberedList {...attributes}>{children}</NumberedList>;
    case 'list-item':
      return <ListItem {...attributes}>{children}</ListItem>;
    case 'heading-one':
      return <HeadingOne {...attributes}>{children}</HeadingOne>;
    case 'heading-two':
      return <HeadingTwo {...attributes}>{children}</HeadingTwo>;
    case 'link':
      return (
        <StyledLink
          {...attributes}
          href={element.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            // Allow link click in read-only mode
            if (!(e.target as HTMLElement).closest('[contenteditable="true"]')) {
              window.open(element.url, '_blank', 'noopener,noreferrer');
            }
          }}
        >
          {children}
        </StyledLink>
      );
    default:
      return <Paragraph {...attributes}>{children}</Paragraph>;
  }
};

// Leaf renderer (for text formatting)
export const Leaf: React.FC<RenderLeafProps> = ({ attributes, children, leaf }) => {
  let formattedChildren = children;

  if (leaf.bold) {
    formattedChildren = <strong>{formattedChildren}</strong>;
  }

  if (leaf.italic) {
    formattedChildren = <em>{formattedChildren}</em>;
  }

  if (leaf.underline) {
    formattedChildren = <u>{formattedChildren}</u>;
  }

  if (leaf.strikethrough) {
    formattedChildren = <s>{formattedChildren}</s>;
  }

  if (leaf.code) {
    formattedChildren = <InlineCode>{formattedChildren}</InlineCode>;
  }

  return <span {...attributes}>{formattedChildren}</span>;
};
